import { Component, computed, effect, input, output, signal } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { NzModalService } from "ng-zorro-antd/modal";
import { ProjectService } from "../../../../shared/services/project.service";
import { CachedProjectService } from "../../../../shared/services/cached-project.service";
import { SharedModule } from "../../../shared.module";
import { rxResource, toSignal } from "@angular/core/rxjs-interop";
import { take } from "rxjs/operators";

@Component({
  selector: "app-definition-form",
  templateUrl: "./definition-form.component.html",
  styleUrl: "./definition-form.component.less",
  standalone: true,
  imports: [SharedModule],
})
export class DefinitionFormComponent {
  name = input.required<string>();
  categories = input.required<string[]>();
  mode = input<"edit" | "add">();
  definitionEdited = output<void>();
  definitionDeleted = output<void>();

  private readonly NEW_CATEGORY_VALUE = "__new_category__";

  protected isAddingCategory = signal(false);
  protected categoryOptions = computed(() => [
    ...this.categories().map((category) => ({ label: category, value: category })),
    { label: "+ New Category", value: this.NEW_CATEGORY_VALUE },
  ]);

  definitionForm: FormGroup;
  isSubmitting = signal(false);
  isDeleting = signal(false);

  // Only fetches in edit mode
  protected definitionResource = rxResource({
    params: () => (this.mode() === "edit" ? { name: this.name() } : undefined),
    stream: ({ params }) => this.cachedProjectService.getParameterDefinition(params.name),
  });

  constructor(
    private fb: FormBuilder,
    private projectService: ProjectService,
    private cachedProjectService: CachedProjectService,
    private notificationService: NzNotificationService,
    private modalService: NzModalService
  ) {
    this.definitionForm = this.fb.group({
      category: this.fb.control<string>("", [Validators.required]),
      arg_type: this.fb.control<string>(""),
      unit: this.fb.control<string>(""),
      default: this.fb.control<string>(""),
      explanation: this.fb.control<string>(""),
      notes: this.fb.control<string>(""),
    });

    const categoryControl = this.definitionForm.controls["category"];
    const categoryValue = toSignal(categoryControl.valueChanges, { initialValue: categoryControl.value });
    effect(() => {
      if (categoryValue() === this.NEW_CATEGORY_VALUE) {
        this.isAddingCategory.set(true);
        categoryControl.setValue("", { emitEvent: false });
      }
    });

    effect(() => {
      const def = this.definitionResource.value();
      if (!def) return;
      this.definitionForm.patchValue({
        category: def.category ?? "",
        arg_type: def.arg_type ?? "",
        unit: def.unit ?? "",
        default: def.default ?? "",
        explanation: def.explanation ?? "",
        notes: def.notes ?? "",
      });
    });

    effect(() => {
      if (this.definitionResource.error()) {
        this.notificationService.error("Error", `Failed to load definition for "${this.name()}".`);
      }
    });
  }

  cancelNewCategory() {
    this.isAddingCategory.set(false);
    this.definitionForm.controls["category"].setValue("");
  }

  submitForm() {
    Object.values(this.definitionForm.controls).forEach((c) => {
      c.markAsDirty();
      c.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });
    if (this.definitionForm.invalid) return;

    const name = this.name();
    const isAdd = this.mode() === "add";
    const request = isAdd
      ? this.projectService.createParameterDefinition({ name, ...this.definitionForm.value })
      : this.projectService.updateParameterDefinition(name, this.definitionForm.value);

    this.isSubmitting.set(true);
    request.pipe(take(1)).subscribe({
      next: () => {
        this.cachedProjectService.clearParameterGlossaryCache(name);
        this.notificationService.success("Success", `Definition for "${name}" was ${isAdd ? "created" : "updated"}.`);
        this.isSubmitting.set(false);
        this.definitionEdited.emit();
      },
      error: (err: any) => {
        this.notificationService.error("Error", err.error?.error || "An unexpected error occured");
        this.isSubmitting.set(false);
      },
    });
  }

  deleteDefinition() {
    const name = this.name();
    this.modalService.confirm({
      nzTitle: `Delete definition for "${name}"?`,
      nzContent: "This cannot be undone.",
      nzOkDanger: true,
      nzOnOk: () => {
        this.isDeleting.set(true);
        this.projectService
          .deleteParameterDefinition(name)
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.cachedProjectService.clearParameterGlossaryCache(name);
              this.notificationService.success("Success", `Definition for "${name}" was deleted.`);
              this.isDeleting.set(false);
              this.definitionDeleted.emit();
            },
            error: (err: any) => {
              this.notificationService.error("Error", err.error?.error || "An unexpected error occured");
              this.isDeleting.set(false);
            },
          });
      },
    });
  }
}
